import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FloorPlanTableMarker from './FloorPlanTableMarker';
import TableCard from './TableCard';
import './FloorPlanView.css';

const GROUP_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];

function FloorPlanView({ tables, orders, alerts, groups, onTableClick }) {
  const { t } = useTranslation();

  const positionedTables = useMemo(
    () => tables.filter((t) => t.positionX != null && t.positionY != null),
    [tables]
  );

  const unpositionedTables = useMemo(
    () => tables.filter((t) => t.positionX == null || t.positionY == null)
      .sort((a, b) => {
        const numA = parseInt(a.tableNumber, 10) || 0;
        const numB = parseInt(b.tableNumber, 10) || 0;
        return numA - numB;
      }),
    [tables]
  );

  // Map table to its group
  const tableGroupMap = useMemo(() => {
    const map = {};
    (groups || []).forEach((group) => {
      group.tableNumbers.forEach((tn) => {
        map[String(tn)] = group;
      });
    });
    return map;
  }, [groups]);

  return (
    <div className="fpv-container">
      <div className="fpv-canvas">
        {/* Group bounding boxes */}
        {(groups || []).map((group, idx) => {
          const groupTables = positionedTables.filter((t) =>
            group.tableNumbers.includes(String(t.tableNumber))
          );
          if (groupTables.length < 2) return null;
          const xs = groupTables.map((t) => t.positionX);
          const ys = groupTables.map((t) => t.positionY);
          const pad = 5;
          const left = Math.max(0, Math.min(...xs) - pad);
          const top = Math.max(0, Math.min(...ys) - pad);
          const right = Math.min(100, Math.max(...xs) + pad);
          const bottom = Math.min(100, Math.max(...ys) + pad);
          const color = GROUP_COLORS[idx % GROUP_COLORS.length];
          return (
            <div
              key={group.groupId}
              className="fpv-group-boundary"
              style={{
                left: `${left}%`, top: `${top}%`,
                width: `${right - left}%`, height: `${bottom - top}%`,
                borderColor: color,
              }}
            >
              <span className="fpv-group-label" style={{ color }}>{group.name}</span>
            </div>
          );
        })}
        {positionedTables.map((table) => (
          <FloorPlanTableMarker
            key={table.tableNumber}
            table={table}
            orders={orders}
            alerts={alerts[String(table.tableNumber)] || {}}
            onClick={onTableClick}
            group={tableGroupMap[String(table.tableNumber)]}
          />
        ))}
      </div>

      {unpositionedTables.length > 0 && (
        <div className="fpv-overflow">
          <span className="fpv-overflow-label">{t('tables.unpositioned', 'Sin ubicar')}</span>
          <div className="fpv-overflow-list">
            {unpositionedTables.map((table) => (
              <TableCard
                key={table.tableNumber}
                table={table}
                orders={orders}
                alerts={alerts[String(table.tableNumber)]}
                onClick={onTableClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FloorPlanView;
